// Folders in the bookmark list: each bookmark is in at most one, shown nested
// under it, and folders collapse. Bookmarks are dragged into and out of
// folders, and folders dragged to reorder them, with @dnd-kit/dom — dnd-kit's
// framework-free layer, since this UI has no framework.
//
// Web UI only for now: the extension sidebar still renders the flat list from
// shared/bookmark-list.ts, which this reuses for the rows themselves.
import { DragDropManager, Draggable, Droppable, Feedback, type DragEndEvent } from '@dnd-kit/dom';
import { renderBookmarkItem, type ListHandlers } from '../../ui/shared/bookmark-list.ts';
import { el } from '../../ui/shared/dom.ts';
import { api, state } from './store.ts';
import type { Bookmark, Folder } from '../../ui/shared/types.ts';

// ── Collapsed state ───────────────────────────────────────────────────────────

// Per browser, not per account: it's a view preference, like the reader's.
const COLLAPSED_KEY = 'altfresh_collapsed_folders';

function loadCollapsed(): Set<number> {
  try {
    const ids: unknown = JSON.parse(localStorage.getItem(COLLAPSED_KEY) || '[]');
    return new Set(Array.isArray(ids) ? ids.filter(Number.isInteger) : []);
  } catch {
    return new Set();
  }
}

const collapsed = loadCollapsed();

function toggleCollapsed(id: number): void {
  collapsed.has(id) ? collapsed.delete(id) : collapsed.add(id);
  try {
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...collapsed]));
  } catch {
    // Private mode or full storage: it just won't be remembered.
  }
}

// ── Drag and drop ─────────────────────────────────────────────────────────────

type DragData = { kind: 'bookmark'; id: number } | { kind: 'folder'; id: number } | { kind: 'top' };

// The drop animation glides the dragged row back to where it came from, which
// reads as "the drop didn't take" when it's about to re-render somewhere else.
const manager = new DragDropManager({
  plugins: (defaults) => defaults.map((p) => (p === Feedback ? Feedback.configure({ dropAnimation: null }) : p)),
});

/** Everything registered by the last render, torn down before the next. */
let entities: (Draggable | Droppable)[] = [];

let onChange: () => void = () => {};

manager.monitor.addEventListener('dragstart', (event) => {
  const data = event.operation.source?.data as DragData | undefined;
  document.body.dataset.dragging = data?.kind ?? '';
});

manager.monitor.addEventListener('dragover', (event) => {
  for (const node of document.querySelectorAll('.drop-target')) node.classList.remove('drop-target');
  const target = event.operation.target;
  if (target && target.id !== event.operation.source?.id) target.element?.classList.add('drop-target');
});

manager.monitor.addEventListener('dragend', (event: DragEndEvent) => {
  delete document.body.dataset.dragging;
  for (const node of document.querySelectorAll('.drop-target')) node.classList.remove('drop-target');
  if (event.canceled) return;
  const source = event.operation.source?.data as DragData | undefined;
  const target = event.operation.target?.data as DragData | undefined;
  if (!source || !target) return;
  // Re-rendering destroys the entities dnd-kit is still finishing this drag
  // with, so the actual change waits for it to let go.
  requestAnimationFrame(() => {
    if (source.kind === 'bookmark') void moveBookmark(source.id, target.kind === 'folder' ? target.id : null);
    else if (source.kind === 'folder' && target.kind === 'folder') void moveFolder(source.id, target.id);
  });
});

function registerDraggable(element: HTMLElement, handle: HTMLElement, data: DragData & { id: number }): void {
  entities.push(new Draggable({ id: `${data.kind}:${data.id}`, type: data.kind, element, handle, data }, manager));
}

function registerDroppable(element: HTMLElement, id: string, accept: string[], data: DragData): void {
  entities.push(new Droppable({ id, element, accept, data }, manager));
}

function grip(label: string): HTMLElement {
  return el('button', { class: 'drag-grip', type: 'button', title: 'drag', 'aria-label': label }, '⠿');
}

// ── Moves ─────────────────────────────────────────────────────────────────────

/**
 * Apply locally first so the drop lands at once, then tell the server; put it
 * back if the server refuses.
 */
async function moveBookmark(id: number, folderId: number | null): Promise<void> {
  const bookmark = state.bookmarks.find((b) => b.id === id);
  if (!bookmark || bookmark.folder_id === folderId) return;
  const previous = bookmark.folder_id;
  bookmark.folder_id = folderId;
  // Dropping into a collapsed folder: open it, so the bookmark doesn't vanish.
  if (folderId !== null && collapsed.has(folderId)) toggleCollapsed(folderId);
  onChange();
  try {
    await api.updateBookmark(id, { folder_id: folderId });
  } catch (err) {
    bookmark.folder_id = previous;
    onChange();
    alert((err as Error).message);
  }
}

async function moveFolder(id: number, targetId: number): Promise<void> {
  const from = state.folders.findIndex((f) => f.id === id);
  const to = state.folders.findIndex((f) => f.id === targetId);
  if (from === -1 || to === -1 || from === to) return;
  const previous = state.folders;
  // Takes the target's place: lands before it when dragged up, after it when
  // dragged down.
  const folders = [...previous];
  folders.splice(to, 0, ...folders.splice(from, 1));
  state.folders = folders;
  onChange();
  try {
    await api.reorderFolders(folders.map((f) => f.id));
  } catch (err) {
    state.folders = previous;
    onChange();
    alert((err as Error).message);
  }
}

/** Move several bookmarks at once (the bulk bar). */
export async function moveBookmarks(ids: number[], folderId: number | null): Promise<void> {
  await api.moveBookmarks(ids, folderId);
  const moving = new Set(ids);
  for (const b of state.bookmarks) if (moving.has(b.id)) b.folder_id = folderId;
  if (folderId !== null && collapsed.has(folderId)) toggleCollapsed(folderId);
}

// ── Folder actions ────────────────────────────────────────────────────────────

export async function createFolder(): Promise<void> {
  const name = prompt('New folder name:')?.trim();
  if (!name) return;
  try {
    const folder = await api.createFolder(name);
    state.folders = [...state.folders, folder];
    onChange();
  } catch (err) {
    alert((err as Error).message);
  }
}

async function renameFolder(folder: Folder): Promise<void> {
  const name = prompt('Rename folder:', folder.name)?.trim();
  if (!name || name === folder.name) return;
  try {
    const renamed = await api.renameFolder(folder.id, name);
    state.folders = state.folders.map((f) => (f.id === folder.id ? renamed : f));
    onChange();
  } catch (err) {
    alert((err as Error).message);
  }
}

async function deleteFolder(folder: Folder): Promise<void> {
  if (!confirm(`Delete folder "${folder.name}"? Its bookmarks move back to the top level.`)) return;
  try {
    await api.deleteFolder(folder.id);
    state.folders = state.folders.filter((f) => f.id !== folder.id);
    for (const b of state.bookmarks) if (b.folder_id === folder.id) b.folder_id = null;
    collapsed.delete(folder.id);
    onChange();
  } catch (err) {
    alert((err as Error).message);
  }
}

// ── Rendering ─────────────────────────────────────────────────────────────────

export interface FolderedListOptions extends ListHandlers {
  /** Re-render after a drop or a folder change. */
  onChange: () => void;
  /** A filter is on: hide folders with no matches and open the rest. */
  filtering: boolean;
}

/**
 * Render `items` (already filtered) grouped by folder, folders first in the
 * user's order, then the bookmarks in none. Returns the ids actually on screen
 * — not those inside a collapsed folder — for the caller's selection pruning.
 */
export function renderFolderedList(
  container: HTMLElement,
  items: Bookmark[],
  options: FolderedListOptions,
): Set<number> {
  for (const entity of entities) entity.destroy();
  entities = [];
  onChange = options.onChange;
  container.replaceChildren();

  const selected = new Set(options.selectedIds || []);
  const shown = new Set<number>();
  const byFolder = new Map<number | null, Bookmark[]>();
  const known = new Set(state.folders.map((f) => f.id));
  for (const b of items) {
    // A folder this client hasn't loaded yet (made in another tab) — show the
    // bookmark at the top level rather than lose it.
    const key = b.folder_id !== null && known.has(b.folder_id) ? b.folder_id : null;
    byFolder.set(key, [...(byFolder.get(key) ?? []), b]);
  }
  const totals = new Map<number, number>();
  for (const b of state.bookmarks) {
    if (b.folder_id !== null) totals.set(b.folder_id, (totals.get(b.folder_id) ?? 0) + 1);
  }

  const row = (bookmark: Bookmark): HTMLElement => {
    shown.add(bookmark.id);
    const item = renderBookmarkItem(bookmark, { ...options, selected: selected.has(bookmark.id) });
    const handle = grip(`Move ${bookmark.title || bookmark.url}`);
    item.querySelector('.bm-head')?.prepend(handle);
    registerDraggable(item, handle, { kind: 'bookmark', id: bookmark.id });
    return item;
  };

  for (const folder of state.folders) {
    const inside = byFolder.get(folder.id) ?? [];
    if (options.filtering && !inside.length) continue;
    const open = options.filtering || !collapsed.has(folder.id);
    const total = totals.get(folder.id) ?? 0;

    const handle = grip(`Move folder ${folder.name}`);
    const section = el('section', { class: `folder${open ? ' open' : ''}`, dataset: { folderId: String(folder.id) } }, [
      el('div', { class: 'folder-head' }, [
        handle,
        el('button', {
          class: 'folder-toggle',
          type: 'button',
          'aria-expanded': String(open),
          disabled: options.filtering,
          onclick: () => {
            toggleCollapsed(folder.id);
            options.onChange();
          },
        }, [
          el('span', { class: 'folder-caret' }, open ? '▾' : '▸'),
          el('span', { class: 'folder-name' }, folder.name),
          el('span', { class: 'folder-count' }, options.filtering ? `${inside.length}/${total}` : String(total)),
        ]),
        el('div', { class: 'folder-actions' }, [
          el('button', { class: 'btn-sm', type: 'button', onclick: () => renameFolder(folder) }, 'rename'),
          el('button', { class: 'btn-sm folder-delete', type: 'button', onclick: () => deleteFolder(folder) }, '✕'),
        ]),
      ]),
      open && el('div', { class: 'folder-body' }, inside.length
        ? inside.map(row)
        : [el('div', { class: 'folder-empty' }, 'empty — drag bookmarks here')]),
    ]);
    container.append(section);
    registerDraggable(section, handle, { kind: 'folder', id: folder.id });
    registerDroppable(section, `into:${folder.id}`, ['bookmark', 'folder'], { kind: 'folder', id: folder.id });
  }

  const loose = byFolder.get(null) ?? [];
  // There whenever any bookmark is in a folder: it's where one is dragged to
  // take it out, even when nothing else is loose.
  if (items.length) {
    const top = el('div', { class: 'top-level' }, loose.length
      ? loose.map(row)
      : [el('div', { class: 'folder-empty' }, 'drop here to take a bookmark out of its folder')]);
    container.append(top);
    if (state.folders.length) registerDroppable(top, 'top', ['bookmark'], { kind: 'top' });
  }

  if (!container.childElementCount) {
    container.append(el('div', { class: 'msg' }, options.emptyMessage || 'nothing here.'));
  }
  return shown;
}
